import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, Menu, X } from "lucide-react";
import { useContent } from "@/context/ContentContext";

export default function Nav() {
    const { content } = useContent();
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location]);

    const phone = content?.phone || "+1 (281) 898-4144";
    const logo = content?.logo_url;

    const links = [
        { href: "#servicios", label: "Services" },
        { href: "#galeria", label: "Gallery" },
        { href: "#nosotros", label: "About" },
        { href: "#testimonios", label: "Reviews" },
        { href: "#contacto", label: "Contact" },
    ];

    return (
        <nav
            data-testid="main-nav"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                scrolled ? "glass-nav py-2" : "bg-transparent py-3"
            }`}
        >
            <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
                <Link to="/" className="flex items-center gap-3" data-testid="nav-logo-link">
                    {logo && (
                        <div className="w-11 h-11 rounded-xl bg-tbv-ink flex items-center justify-center overflow-hidden">
                            <img src={logo} alt="Three Brothers Vivero" className="w-full h-full object-contain" />
                        </div>
                    )}
                    <div className="leading-tight hidden sm:block">
                        <div className="font-condensed text-lg text-tbv-ink">THREE BROTHERS</div>
                        <div className="font-condensed text-xs text-tbv-deep -mt-1">NURSERY · EAST BERNARD, TX</div>
                    </div>
                </Link>

                <div className="hidden lg:flex items-center gap-8">
                    {links.map((l) => (
                        <a
                            key={l.href}
                            href={l.href}
                            data-testid={`nav-link-${l.label.toLowerCase()}`}
                            className="text-sm font-medium text-tbv-ink hover:text-tbv-deep transition-colors"
                        >
                            {l.label}
                        </a>
                    ))}
                </div>

                <div className="flex items-center gap-3">
                    <a
                        href={`tel:${phone.replace(/[^+0-9]/g, "")}`}
                        data-testid="nav-call-button"
                        className="btn-primary text-sm hidden sm:inline-flex"
                    >
                        <Phone className="w-4 h-4" />
                        {phone}
                    </a>
                    <button
                        data-testid="nav-mobile-toggle"
                        onClick={() => setOpen(!open)}
                        className="lg:hidden p-2 rounded-lg border border-tbv-border bg-white"
                        aria-label="Menu"
                    >
                        {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                    </button>
                </div>
            </div>

            {open && (
                <div className="lg:hidden mt-3 mx-4 rounded-2xl border border-tbv-border bg-white p-5 shadow-lg">
                    <div className="flex flex-col gap-3">
                        {links.map((l) => (
                            <a
                                key={l.href}
                                href={l.href}
                                data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                                className="text-base font-medium py-2 border-b border-tbv-border"
                            >
                                {l.label}
                            </a>
                        ))}
                        <a
                            href={`tel:${phone.replace(/[^+0-9]/g, "")}`}
                            className="btn-primary mt-2 justify-center"
                            data-testid="nav-mobile-call"
                        >
                            <Phone className="w-4 h-4" /> {phone}
                        </a>
                    </div>
                </div>
            )}
        </nav>
    );
}
